import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { cancelShipments } from '@/features/shipment/api'
import { orderListKeys } from '@/features/order-list/keys'

import {
  fetchGroupAddress,
  fetchGroupDetail,
  fetchGroupOrderCount,
  fetchGroups,
  saveGroupAddress,
  cancelGroupOrder,
  deleteGroupOrder,
  deleteGroupOrders,
  createGroupOrder,
  editGroupOrder,
  chargeGroupOrder,
  downloadGroupOrderLabels,
  processGroupOrders,
  fetchOrderItemChatHistory,
  fetchProductNameSuggestions,
} from './api'
import type {
  EditGroupOrderInput,
  ChargeGroupOrderInput,
  CreateGroupOrderInput,
} from './order-charge'
import { serializeOrderStatuses, type OrderFilter } from './order-tabs'
import type { LabelPrintType } from './label-type'
import { removeDeletedOrders } from './delete-orders-cache'

interface OrderQueryParams extends OrderFilter {
  groupId: string
  search?: string
  page?: number
  perPage?: number
}

export const GROUP_LIST_PAGE_SIZE = 10

export const groupKeys = {
  all: ['group'] as const,
  lists: () => [...groupKeys.all, 'list'] as const,
  list: (page: number) => [...groupKeys.lists(), page] as const,
  listInfinite: (perPage: number) => [...groupKeys.lists(), 'infinite', perPage] as const,
  details: () => [...groupKeys.all, 'detail'] as const,
  detail: (
    groupId: string,
    orderStatus: string,
    invoiceStatus: string,
    search: string,
    page: number,
    perPage: number,
  ) =>
    [...groupKeys.details(), groupId, orderStatus, invoiceStatus, search, page, perPage] as const,
  detailInfinite: (
    groupId: string,
    orderStatus: string,
    invoiceStatus: string,
    search: string,
    perPage: number,
  ) =>
    [
      ...groupKeys.details(),
      groupId,
      orderStatus,
      invoiceStatus,
      search,
      'infinite',
      perPage,
    ] as const,
  counts: () => [...groupKeys.all, 'count'] as const,
  count: (groupId: string, orderStatus: string, invoiceStatus: string, search: string) =>
    [...groupKeys.counts(), groupId, orderStatus, invoiceStatus, search] as const,
  address: (groupId: string) => [...groupKeys.all, 'address', groupId] as const,
  chatHistory: (groupId: string, orderId: string, itemIndex: number) =>
    [...groupKeys.all, 'chat-history', groupId, orderId, itemIndex] as const,
  productNameSuggestions: (groupId: string, query: string, limit: number) =>
    [...groupKeys.all, 'product-name-suggestions', groupId, query, limit] as const,
}

export function groupsQueryOptions(page = 1, perPage = GROUP_LIST_PAGE_SIZE) {
  return queryOptions({
    queryKey: groupKeys.list(page),
    queryFn: ({ signal }) => fetchGroups({ page, perPage, signal }),
  })
}

export function groupsInfiniteQueryOptions(perPage = GROUP_LIST_PAGE_SIZE, source?: string) {
  return infiniteQueryOptions({
    queryKey: source
      ? [...groupKeys.listInfinite(perPage), { source }] as const
      : groupKeys.listInfinite(perPage),
    queryFn: ({ pageParam, signal }) => fetchGroups({ page: pageParam, perPage, source, signal }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNext ? lastPage.pagination.page + 1 : undefined,
  })
}

export function whatsAppGroupsQueryOptions() {
  return groupsInfiniteQueryOptions(GROUP_LIST_PAGE_SIZE, 'WHATSAPP')
}

export function groupDetailQueryOptions({
  groupId,
  orderStatuses,
  invoiceStatus,
  search = '',
  page = 1,
  perPage = 100,
}: OrderQueryParams) {
  const orderStatus = serializeOrderStatuses(orderStatuses)

  return queryOptions({
    queryKey: groupKeys.detail(groupId, orderStatus, invoiceStatus ?? '', search, page, perPage),
    queryFn: ({ signal }) =>
      fetchGroupDetail({ groupId, orderStatus, invoiceStatus, search, page, perPage, signal }),
    // Keep the previous rows on screen while the next filter loads, so switching
    // tabs or typing dims the list instead of collapsing it into a skeleton.
    placeholderData: keepPreviousData,
  })
}

export function groupDetailInfiniteQueryOptions({
  groupId,
  orderStatuses,
  invoiceStatus,
  search = '',
  perPage,
}: OrderQueryParams & { perPage: number }) {
  const orderStatus = serializeOrderStatuses(orderStatuses)

  return infiniteQueryOptions({
    queryKey: groupKeys.detailInfinite(groupId, orderStatus, invoiceStatus ?? '', search, perPage),
    queryFn: ({ pageParam, signal }) =>
      fetchGroupDetail({
        groupId,
        orderStatus,
        invoiceStatus,
        search,
        page: pageParam,
        perPage,
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNext ? lastPage.pagination.page + 1 : undefined,
  })
}

export function groupOrderCountQueryOptions({
  groupId,
  orderStatuses,
  invoiceStatus,
  search = '',
}: OrderQueryParams) {
  const orderStatus = serializeOrderStatuses(orderStatuses)

  return queryOptions({
    queryKey: groupKeys.count(groupId, orderStatus, invoiceStatus ?? '', search),
    queryFn: ({ signal }) =>
      fetchGroupOrderCount({ groupId, orderStatus, invoiceStatus, search, signal }),
    placeholderData: keepPreviousData,
  })
}

export const PRODUCT_NAME_SUGGESTION_MIN_QUERY = 3

const PRODUCT_NAME_SUGGESTION_LIMIT = 10

export function productNameSuggestionsQueryOptions(groupId: string, query: string) {
  const trimmed = query.trim()
  return queryOptions({
    queryKey: groupKeys.productNameSuggestions(groupId, trimmed, PRODUCT_NAME_SUGGESTION_LIMIT),
    queryFn: ({ signal }) =>
      fetchProductNameSuggestions({
        groupId,
        query: trimmed,
        limit: PRODUCT_NAME_SUGGESTION_LIMIT,
        signal,
      }),
    enabled: trimmed.length >= PRODUCT_NAME_SUGGESTION_MIN_QUERY,
    staleTime: 0,
  })
}

export function groupAddressQueryOptions(groupId: string) {
  return queryOptions({
    queryKey: groupKeys.address(groupId),
    queryFn: ({ signal }) => fetchGroupAddress(groupId, signal),
    staleTime: 60_000,
  })
}

export function orderItemChatHistoryQueryOptions(
  groupId: string,
  orderId: string,
  itemIndex: number,
  itemArrayIndex: number | undefined,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: groupKeys.chatHistory(groupId, orderId, itemIndex),
    queryFn: ({ signal }) =>
      fetchOrderItemChatHistory(groupId, orderId, itemIndex, itemArrayIndex, signal),
    enabled,
    staleTime: 0,
  })
}

export function useSaveGroupAddress(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Omit<Parameters<typeof saveGroupAddress>[0], 'groupId'>) =>
      saveGroupAddress({ ...input, groupId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupKeys.address(groupId) })
      void queryClient.invalidateQueries({ queryKey: [...groupKeys.details(), groupId] })
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}

export function useCancelGroupOrder(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, shipmentId }: { orderId: string; shipmentId?: string | null }) => {
      const shipmentCancelId = shipmentId?.trim()
      if (shipmentCancelId) return cancelShipments([shipmentCancelId])
      return cancelGroupOrder(groupId, orderId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orderListKeys.all })
      void queryClient.invalidateQueries({ queryKey: groupKeys.details() })
      void queryClient.invalidateQueries({ queryKey: groupKeys.counts() })
    },
  })
}

export function useDeleteGroupOrder(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orderId: string) => deleteGroupOrder(groupId, orderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orderListKeys.all })
      void queryClient.invalidateQueries({ queryKey: groupKeys.details() })
      void queryClient.invalidateQueries({ queryKey: groupKeys.counts() })
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}

export function useDeleteGroupOrders(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orderIds: string[]) => deleteGroupOrders(groupId, orderIds),
    retry: false,
    onSuccess: async (result) => {
      const queryKey = [...groupKeys.details(), groupId]
      await queryClient.cancelQueries({ queryKey })
      const deletedIds = new Set(result.deleted_order_external_ids)
      queryClient.setQueriesData({ queryKey }, (current: unknown) =>
        removeDeletedOrders(current, deletedIds),
      )
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: orderListKeys.all })
      void queryClient.invalidateQueries({ queryKey: [...groupKeys.details(), groupId] })
      void queryClient.invalidateQueries({ queryKey: [...groupKeys.counts(), groupId] })
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}

export function useCreateGroupOrder(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateGroupOrderInput) => createGroupOrder(groupId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orderListKeys.all })
      void queryClient.invalidateQueries({ queryKey: groupKeys.details() })
      void queryClient.invalidateQueries({ queryKey: groupKeys.counts() })
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}

export function useEditGroupOrder(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: EditGroupOrderInput }) =>
      editGroupOrder(groupId, orderId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orderListKeys.all })
      void queryClient.invalidateQueries({ queryKey: groupKeys.details() })
      void queryClient.invalidateQueries({ queryKey: groupKeys.counts() })
    },
  })
}

export function useSendGroupInvoice(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      orderId,
      patch,
      charge,
    }: {
      orderId: string
      patch?: EditGroupOrderInput
      charge: ChargeGroupOrderInput
    }) => {
      if (patch) await editGroupOrder(groupId, orderId, patch)
      return chargeGroupOrder(groupId, orderId, charge)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orderListKeys.all })
    },
  })
}

export function useResendGroupInvoice(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, charge }: { orderId: string; charge: ChargeGroupOrderInput }) =>
      chargeGroupOrder(groupId, orderId, charge),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orderListKeys.all })
      void queryClient.invalidateQueries({ queryKey: groupKeys.details() })
      void queryClient.invalidateQueries({ queryKey: groupKeys.counts() })
    },
  })
}

export function useProcessGroupOrders(groupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orderExternalIds: string[]) => processGroupOrders(groupId, orderExternalIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orderListKeys.all })
      void queryClient.invalidateQueries({ queryKey: groupKeys.details() })
      void queryClient.invalidateQueries({ queryKey: groupKeys.counts() })
      void queryClient.invalidateQueries({ queryKey: groupKeys.lists() })
    },
  })
}

export function useDownloadGroupOrderLabels(groupId: string) {
  return useMutation({
    mutationFn: (orderExternalIds: string[]) => downloadGroupOrderLabels(groupId, orderExternalIds),
  })
}

export function groupOrderLabelsQueryOptions(
  groupId: string,
  orderIds: string[],
  printType: LabelPrintType,
) {
  return queryOptions({
    queryKey: [...groupKeys.all, 'labels', groupId, orderIds, printType],
    queryFn: ({ signal }) => downloadGroupOrderLabels(groupId, orderIds, signal, printType),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
    gcTime: 0,
  })
}
